import { type CSSProperties, useContext, useState } from "react";
import DialogChatAPI from "../../../api/dialogChatApi";
import TripleDotPoperMenu from "../../../components/TripleDotPoperMenu/TripleDotPoperMenu";
import CircleAvatar from "../../../components/CircleAvatar/CircleAvatar";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import Input from "../../../components/Input/Input";
import ModalWindow from "../../../components/ModalWindow/ModalWindow";
import { ContextProvider } from "../../../contextProvider";
import strings from "../../../strings";
import { DialogChat } from "../../../types/DialogChat";
import { getTranslation } from "../../../utils/getTranslation";
import { getGlobalChatConversationActionAvailability } from "../model/globalChatConversationActions";
import s from "./ChatHeaderMenu.module.css";

type ChatHeaderMenuProps = {
  chat: DialogChat;
  onSelectChat: (chat: DialogChat | null) => void;
  onUpsertChat: (chat: DialogChat, position?: "start" | "end") => void;
  onPinChat: (chat: DialogChat) => void;
  onUnpinChat: (chat: DialogChat) => void;
  onToggleMuteChat: (chat: DialogChat) => void;
  onToggleBlockChat: (chat: DialogChat) => void;
  onLeaveChat: (chat: DialogChat) => void;
};

const ChatHeaderMenu = ({
  chat,
  onSelectChat,
  onUpsertChat,
  onPinChat,
  onUnpinChat,
  onToggleMuteChat,
  onToggleBlockChat,
  onLeaveChat,
}: ChatHeaderMenuProps) => {
  const token = localStorage.getItem("token");
  const { userData } = useContext(ContextProvider);
  const currentUserId = userData?._id;

  const [participantsModal, setParticipantsModal] = useState(false);
  const [participantsSearch, setParticipantsSearch] = useState("");
  const [confirmModals, setConfirmModals] = useState({
    leaveChat: false,
    blockChat: false,
  });

  const setConfirmModalVisible = (
    key: "leaveChat" | "blockChat",
    isOpen: boolean,
  ) => {
    setConfirmModals((prev) => ({ ...prev, [key]: isOpen }));
  };

  const getMemberFullName = (member: DialogChat["members"][number]) => {
    return `${getTranslation(member.name, userData?.selectedLanguage)} ${getTranslation(
      member.surname,
      userData?.selectedLanguage,
    )}`;
  };

  const isCurrentUserMuted = (chat.mutedBy || []).includes(currentUserId ?? "");
  const isCurrentUserBlocked = (chat.blockedBy || []).includes(
    currentUserId ?? "",
  );
  const actionAvailability = getGlobalChatConversationActionAvailability(chat, {
    currentUserId,
    adminUserId: process.env.REACT_APP_ADMIN_ID,
  });

  const getMenuItems = () =>
    [
      actionAvailability.participants && {
        title: strings.viewParticipants,
        action: () => {
          setParticipantsModal(true);
        },
      },
      actionAvailability.pin && {
        title: strings.pinChat,
        action: () => onPinChat(chat),
      },
      actionAvailability.unpin && {
        title: strings.unpinChat,
        action: () => onUnpinChat(chat),
      },
      (actionAvailability.mute || actionAvailability.unmute) && {
        title: isCurrentUserMuted
          ? strings.enableNotifications
          : strings.disableNotifications,
        action: () => {
          onToggleMuteChat(chat);
        },
      },
      (actionAvailability.block || actionAvailability.unblock) && {
        title: isCurrentUserBlocked ? strings.unblockChat : strings.blockChat,
        action: () => {
          setConfirmModalVisible("blockChat", true);
        },
      },
      actionAvailability.leave && {
        title: strings.leaveChat,
        action: () => {
          setConfirmModalVisible("leaveChat", true);
        },
        styles: { color: "var(--main-red)" },
      },
    ].filter(Boolean) as {
      title: string;
      action: () => void;
      disabled?: boolean;
      styles?: CSSProperties;
    }[];

  const openNewChat = async (userId: string) => {
    if (!token || !currentUserId) return;
    setParticipantsModal(false);
    const response = await DialogChatAPI.createDialogChat(token, {
      userId: currentUserId,
      userIdToSend: userId,
    });
    if (response.status && response.chat) {
      onUpsertChat(response.chat, "start");
      onSelectChat(response.chat);
    }
  };
  const speakerIds = [
    chat.webinar?.createdBy,
    chat.webinar?.coSpeakerUser,
  ].filter(Boolean) as string[];

  const myRole = chat.webinar
    ? speakerIds.includes(currentUserId ?? "")
      ? "admin"
      : "participant"
    : "contact";

  const normalizedParticipantsSearch = participantsSearch.trim().toLowerCase();

  const filteredMembers = chat.members.filter((member) => {
    if (!normalizedParticipantsSearch) {
      return true;
    }

    const fullName = getMemberFullName(member).toLowerCase();

    return fullName.includes(normalizedParticipantsSearch);
  });

  return (
    <div>
      <TripleDotPoperMenu menuItemDta={getMenuItems()} />
      <ModalWindow
        width="fit-content"
        isOpen={participantsModal}
        setIsOpen={(isOpen) => {
          setParticipantsModal(isOpen);
          if (!isOpen) {
            setParticipantsSearch("");
          }
        }}
        title={strings.participantsTitle}
      >
        <div className={s.participantsContent}>
          <Input
            onChangeInput={setParticipantsSearch}
            inputValue={participantsSearch}
            placeholder={strings.searchParticipants}
            isVisible
          />

          <div className={s.participantsContainer}>
            {filteredMembers.map((member) => {
              const fullName = getMemberFullName(member);
              const memberRole = speakerIds.includes(member._id)
                ? strings.speaker
                : strings.participant;

              return (
                <div className={s.participantRow} key={member._id}>
                  <div className={s.participantInfo}>
                    <CircleAvatar
                      userId={member._id}
                      width="42px"
                      height="42px"
                      fontSize="18px"
                    />
                    <div className={s.participantTextBlock}>
                      <span className={s.participantName}>{fullName}</span>
                      <span className={s.participantRole}>{memberRole}</span>
                    </div>
                  </div>

                  {myRole === "admin" && member._id !== userData?._id && (
                    <button
                      type="button"
                      className={s.privateChatButton}
                      onClick={() => openNewChat(member._id)}
                    >
                      {strings.write}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </ModalWindow>
      <ConfirmationModal
        confirmFunc={() => {
          setConfirmModalVisible("leaveChat", false);
          onLeaveChat(chat);
        }}
        modalVisible={confirmModals.leaveChat}
        setModalVisible={(value) =>
          setConfirmModalVisible(
            "leaveChat",
            typeof value === "function" ? value(confirmModals.leaveChat) : value,
          )
        }
        confirmTitle={strings.confirmLeaveChat}
        confirmString={strings.leave}
      />
      <ConfirmationModal
        confirmFunc={() => {
          setConfirmModalVisible("blockChat", false);
          onToggleBlockChat(chat);
        }}
        modalVisible={confirmModals.blockChat}
        setModalVisible={(value) =>
          setConfirmModalVisible(
            "blockChat",
            typeof value === "function" ? value(confirmModals.blockChat) : value,
          )
        }
        confirmTitle={
          isCurrentUserBlocked
            ? strings.confirmUnblockChat
            : strings.confirmBlockChat
        }
        confirmString={isCurrentUserBlocked ? strings.unblock : strings.block}
      />
    </div>
  );
};

export default ChatHeaderMenu;
